import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Calendar, Phone, User, MapPin, Building2, MessageSquare, Clock, CheckCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";

const consultationSchema = z.object({
  student_name: z.string().min(2, "Student name must be at least 2 characters").max(100),
  parent_name: z.string().max(100).optional(),
  city_in_india: z.string().min(2, "City is required").max(100),
  preferred_intake: z.string().min(1, "Please select an intake"),
  university_or_course: z.string().min(2, "University/Course is required").max(200),
  whatsapp_student: z.string().min(10, "Valid WhatsApp number required").max(20),
  whatsapp_parent: z.string().max(20).optional(),
  parents_joining: z.string().min(1, "Please select an option"),
  preparation_level: z.string().min(1, "Please select your preparation level"),
  consultation_reason: z.string().min(10, "Please provide a reason (at least 10 characters)").max(500),
  preferred_datetime: z.string().min(1, "Please select a preferred date/time"),
  urgency_level: z.string().min(1, "Please select your urgency level"),
});

type ConsultationFormValues = z.infer<typeof consultationSchema>;

const ConsultationBooking = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ConsultationFormValues>({
    resolver: zodResolver(consultationSchema),
    defaultValues: {
      student_name: "",
      parent_name: "",
      city_in_india: "",
      preferred_intake: "",
      university_or_course: "",
      whatsapp_student: "",
      whatsapp_parent: "",
      parents_joining: "",
      preparation_level: "",
      consultation_reason: "",
      preferred_datetime: "",
      urgency_level: "",
    },
  });

  const onSubmit = async (values: ConsultationFormValues) => {
    setIsSubmitting(true);
    try {
      // Save to database
      const { error } = await supabase
        .from('consultation_bookings')
        .insert({
          student_name: values.student_name,
          parent_name: values.parent_name || null,
          city_in_india: values.city_in_india,
          preferred_intake: values.preferred_intake,
          university_or_course: values.university_or_course,
          whatsapp_student: values.whatsapp_student,
          whatsapp_parent: values.whatsapp_parent || null,
          parents_joining: values.parents_joining,
          preparation_level: values.preparation_level,
          consultation_reason: values.consultation_reason,
          preferred_datetime: values.preferred_datetime,
          urgency_level: values.urgency_level,
        });

      if (error) throw error;

      // Send email notification (don't block on failure)
      supabase.functions.invoke('send-notification-email', {
        body: {
          type: 'consultation',
          data: values
        }
      }).catch(err => console.error('Email notification failed:', err));

      setSubmitted(true);
      form.reset();
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast.error("Submission Failed", {
        description: "Please try again or contact us directly at +61 0403 601 970."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12 px-4 flex items-center justify-center">
          <div className="container mx-auto max-w-2xl">
            <Card className="shadow-elegant text-center">
              <CardContent className="pt-12 pb-12">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Thank You for Submitting!
                </h2>
                <p className="text-muted-foreground mb-6">
                  We'll get back to you shortly. For more information, you can contact us via WhatsApp.
                </p>
                <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#20BA5A]">
                  <a 
                    href="https://wa.me/+61403601970" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                    <MessageSquare className="w-5 h-5" />
                    Chat with Us on WhatsApp
                  </a>
                </Button>
                <div className="mt-6">
                  <Button 
                    variant="outline" 
                    onClick={() => setSubmitted(false)}
                  >
                    Submit Another Booking
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Book a <span className="text-secondary">Free Consultation Call</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Speak directly with our founders. Limited slots available every week.
            </p>
            <p className="text-sm text-muted-foreground italic max-w-2xl mx-auto bg-muted/50 p-4 rounded-lg">
              <strong>Note:</strong> Consultation calls are free, but slots are limited. We prioritise students with upcoming intakes and parents who want clear guidance on arrival and settlement in Australia.
            </p>
          </div>

          <Card className="shadow-elegant animate-fade-in" style={{ animationDelay: "0.1s" }}>
            <CardHeader>
              <CardDescription className="text-center text-base">
                Consultation calls are limited and reserved for serious students planning to travel in upcoming intakes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="student_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Student Full Name *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input placeholder="Enter student's full name" className="pl-10" {...field} />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="parent_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Parent Full Name</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input placeholder="Optional but recommended" className="pl-10" {...field} />
                            </div>
                          </FormControl>
                          <FormDescription>Optional but recommended</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="city_in_india"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City in India *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input placeholder="Enter your city" className="pl-10" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preferred_intake"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Intake / Arrival Month *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select intake" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="feb-2025">Feb 2025</SelectItem>
                            <SelectItem value="july-2025">July 2025</SelectItem>
                            <SelectItem value="nov-2025">Nov 2025</SelectItem>
                            <SelectItem value="researching">Just researching</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="university_or_course"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>University or Course You're Targeting *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input placeholder="Enter university or course" className="pl-10" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="whatsapp_student"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>WhatsApp Number (Student) *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input placeholder="+91XXXXXXXXXX" className="pl-10" {...field} />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="whatsapp_parent"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>WhatsApp Number (Parent)</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                              <Input placeholder="+91XXXXXXXXXX (optional)" className="pl-10" {...field} />
                            </div>
                          </FormControl>
                          <FormDescription>Optional but ideal</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="parents_joining"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Are your parents joining the call? *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select option" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                            <SelectItem value="maybe">Maybe</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preparation_level"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>How prepared are you for Australia right now? *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select your preparation level" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="not-prepared">Not prepared at all</SelectItem>
                            <SelectItem value="somewhat-prepared">Somewhat prepared</SelectItem>
                            <SelectItem value="need-guidance">Need full guidance</SelectItem>
                            <SelectItem value="have-support">Already have support but need Ace Anchor</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="consultation_reason"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Why do you want this consultation? *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="I want help with arrival, accommodations, or guidance."
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>Tell us what you need help with (at least 10 characters)</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preferred_datetime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Date & Time for the Call *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Clock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              type="datetime-local"
                              className="pl-10"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="urgency_level"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Urgency Level *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select urgency level" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="30-days">Landing in 30 days</SelectItem>
                            <SelectItem value="60-90-days">Landing in 60–90 days</SelectItem>
                            <SelectItem value="3-months-plus">Landing after 3 months</SelectItem>
                            <SelectItem value="still-planning">Still planning</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <MessageSquare className="inline-block w-4 h-4 mr-2" />
                      <strong>After submitting this form,</strong> our team will contact you on WhatsApp to confirm your call.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Booking..." : "Book My Free Consultation"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ConsultationBooking;
